/**
 * Product Configuration - Single Source of Truth
 * 
 * This file defines all products, services, and their metadata.
 * DO NOT hardcode product information elsewhere.
 */

/**
 * INTERNAL PRODUCTS - Users can signup here, have roles, access features
 */
export const PRODUCTS = {
  FRAUD_PREVENTION: {
    id: 'fraud-prevention',
    name: 'Fraud Prevention System',
    displayName: 'Fraud Prevention',
    path: '/fraud-prevention',
    consolePath: '/fraud/submit',
    roles: ['USER', 'INVESTIGATOR', 'MEDIATOR', 'EVIDENCE_SCOUT', 'CERTIFICATION_AUTHORITY'],
    icon: 'SecurityIcon',
    isCore: true,  // Built-in product
    hasAdminPanel: true,
    adminPath: '/admin/fraud',
  },
  DISTRO_ENGINE: {
    id: 'distro-engine',
    name: 'DistroEngine',
    displayName: 'DistroEngine',
    path: '/distro-engine',
    consolePath: '/console/distro-engine',
    roles: ['DISTRO_CREATOR'],
    icon: 'GrainIcon',
    hasAdminPanel: true,
    adminPath: '/admin/distro',
  },
  CAPTION_ENGINE: {
    id: 'caption-engine',
    name: 'CaptionEngine',
    displayName: 'CaptionEngine',
    path: '/caption-engine',
    consolePath: '/console/caption-engine',
    roles: ['BUSINESS_CONTENT_CREATOR'],
    icon: 'CampaignIcon',
    hasAdminPanel: true,
    adminPath: '/admin/caption-engine',
  },
  GUARDIAN_CLI: {
    id: 'guardian-cli',
    name: 'Guardian CLI',
    displayName: 'Guardian CLI',
    path: '/cli-docs',
    consolePath: '/cli-docs',
    roles: ['CLI_USER'],  // Users who have paid for CLI access
    icon: 'TerminalIcon',
    isInfra: true,
    hasAdminPanel: true,
    adminPath: '/admin/infra',
    description: 'Infrastructure deployment tool with subscription-based licensing',
  },
  GOVERNABLE_AI: {
    id: 'governable-ai',
    name: 'Governable AI Workspace',
    displayName: 'Governable AI',
    path: '/governable-ai',
    consolePath: '/governable-ai/workspace',
    roles: ['USER'],
    icon: 'FactCheck',
    hasAdminPanel: false,
    description: 'AI governance inventory, risk classification, evidence files, approvals, redress, and audit-ready exports.',
  },
  FAMILY_CARE: {
    id: 'family-care',
    name: 'Family Care',
    displayName: 'Family Care',
    path: '/family-care',
    consolePath: '/family-care/bookings',
    roles: ['CAREGIVER'],
    icon: 'ChildCareIcon',
    hasAdminPanel: true,
    adminPath: '/admin/family-care',
    description: 'In-home child care booking, caregiver management, and payroll.',
  },
} as const;

/**
 * REFERRAL PROGRAM - NOT a subscription product
 * Commission-based program for sales agents
 * Separate from subscription-based products
 */
export const REFERRAL_PROGRAM = {
  id: 'referral-program',
  name: 'Referral Program',
  displayName: 'Referral Program',
  path: '/referral-program',
  consolePath: '/sales/dashboard',
  roles: ['SALES_AGENT'],
  icon: 'PeopleIcon',
  hasAdminManagement: true,
  adminManagementPath: '/admin/sales',
  description: 'Commission-based sales program (NOT subscription-based)',
} as const;

/**
 * EXTERNAL PRODUCTS - Separate backends, we only collect waitlist opt-ins
 * Users exist in external systems, NOT in Guardian AI
 */
export const EXTERNAL_PRODUCTS = {
  FELLOWVIA: {
    id: 'fellowvia',
    name: 'Fellowvia',
    displayName: 'Fellowvia',
    path: '/showroom/fellowvia',
    externalApp: true,
    loginUrl: 'https://fellowvia.guardianai.network/sign-in',
    externalCtaUrl: 'https://fellowvia.guardianai.network/sign-in',
    wixInstallUrl: 'https://wix.to/myH3z1e',
    icon: 'MenuBook',
    badge: 'Faith & Ministry SaaS',
    tagline: 'Daily devotionals & 98% open-rate church SMS broadcasts.',
    description: 'Equip ministry leaders to publish daily devotionals and engage congregations through private, distraction-free SMS broadcasts.',
  },
  CARECONFIRM: {
    id: 'careconfirm',
    name: 'CareConfirm',
    displayName: 'CareConfirm',
    path: 'https://careconfirm.guardianai.network',
    externalApp: true,
    loginUrl: 'https://careconfirm.guardianai.network/login',
    externalCtaUrl: 'https://careconfirm.guardianai.network',
    icon: 'ChildCare',
    badge: 'Childcare & Daycare SaaS',
    tagline: 'Childcare attendance, booking, roster management & parent communication.',
    description: 'Streamline daily childcare attendance, real-time booking rosters, automated parent confirmations, and billing compliance for daycare centers.',
  },
  COMMERCE_RECOVERY: {
    id: 'commerce-recovery',
    name: 'Guardian Commerce Recovery',
    displayName: 'Commerce Recovery',
    path: 'https://recovery.guardianai.network/',
    externalApp: true,
    loginUrl: 'https://recovery.guardianai.network/',
    externalCtaUrl: 'https://recovery.guardianai.network/',
    guideUrl: 'https://recovery.guardianai.network/#/guide',
    icon: 'Storefront',
    badge: 'E-Commerce & Shopify SaaS',
    tagline: 'Automated Shopify chargeback defense, dispute evidence & revenue recovery.',
    description: 'Automated Shopify dispute defense, evidence collection, and revenue recovery engine for high-volume merchants.',
  },
  SITE_RECOVERY: {
    id: 'site-recovery',
    name: 'Guardian Site Recovery',
    displayName: 'Site Recovery',
    path: 'https://siterecovery.guardianai.network/',
    externalApp: true,
    loginUrl: 'https://siterecovery.guardianai.network/#/login',
    registerUrl: 'https://siterecovery.guardianai.network/#/register',
    externalCtaUrl: 'https://siterecovery.guardianai.network/',
    icon: 'Shield',
    badge: 'Malware & Breach Recovery',
    tagline: 'Supervised hacked website & malware remediation with zero black-box deletions.',
    description: 'Detect webshells and backdoors, correlate ingress vectors from server logs, review cryptographic diffs, and safely execute verified malware cleanup with rollback protection.',
  },
  MEDIA_PROOF: {
    id: 'media-proof',
    name: 'Media Proof',
    displayName: 'Media Proof',
    path: '/media-proof',
    externalApp: true,
    externalCtaUrl: 'https://media-proof.app',
    icon: 'VerifiedUserIcon',
    isWaitlist: true,
    description: 'Mobile app with own backend. Collects waitlist opt-ins only.',
  },
  NATIVE_TONGUE: {
    id: 'native-tongue',
    name: 'Native Tongue',
    displayName: 'Native Tongue',
    path: '/native-tongue',
    externalApp: true,
    externalCtaUrl: 'https://native-tongue.app',
    icon: 'TranslateIcon',
    isWaitlist: true,
    description: 'Web app with own backend. Collects waitlist opt-ins only.',
  },
} as const;

/**
 * PRODUCTS IN DEVELOPMENT - Auth model and user flows TBD
 * DO NOT make assumptions about signup or roles until auth model is decided
 */
export const PRODUCTS_IN_DEVELOPMENT = {
  RAG_INTEGRITY_SENTINEL: {
    id: 'rag-integrity-sentinel',
    name: 'Rag Integrity Sentinel',
    displayName: 'Rag Integrity Sentinel',
    path: '/rag-integrity-sentinel',
    inDevelopment: true,
    authModelTBD: true,
    icon: 'CheckCircleIcon',
    description: 'Still in development. Auth model and user flows TBD.',
  },
} as const;

/**
 * SERVICES - Non-subscription tools
 * Include developer tools and other services that don't require subscriptions
 */
export const SERVICES = {
  SANDBOX: {
    id: 'sandbox',
    name: 'Sandbox',
    displayName: 'Sandbox',
    path: '/sandbox',
    isDevTool: true,
    icon: 'SandboxIcon',
    description: 'Development and testing environment.',
  },
  AI_AGENTS: {
    id: 'ai-agents',
    name: 'AI Agents',
    displayName: 'AI Agents',
    path: '/ai-agent',
    icon: 'SmartToyIcon',
    description: 'AI Agent hub and documentation.',
  },
} as const;

/**
 * CLI COMPONENTS - Device-based tools that sync with backend subscriptions
 * 
 * These are binaries that run on user's device but sync subscription paywalls
 * and licensing with the backend to keep everything in lockstep.
 * 
 * Examples:
 * - Guardian CLI: Infrastructure deployment tool, runs on user's machine, checks Guardian CLI subscription status
 * - DistroEngine CLI: Distribution management tool, runs on user's machine, checks DistroEngine subscription status
 */
export const CLI_COMPONENTS = {
  GUARDIAN_CLI_BINARY: {
    id: 'guardian-cli-binary',
    name: 'Guardian CLI Binary',
    description: 'CLI tool for infrastructure deployment. Syncs licensing with Guardian CLI subscription.',
    linkedProduct: 'guardian-cli',  // Linked to Guardian CLI product subscription
    installUrl: 'https://github.com/LuckyOkoedion/guardian-ai-smart-multi-cloud-infra/releases',
  },
  DISTRO_ENGINE_CLI_BINARY: {
    id: 'distro-engine-cli-binary',
    name: 'DistroEngine CLI Binary',
    description: 'CLI tool for distribution management. Syncs licensing with DistroEngine subscription.',
    linkedProduct: 'distro-engine',  // Linked to DistroEngine product subscription
    installUrl: 'https://github.com/guardianai-network/distroengine-cli/releases',
  },
} as const;

/**
 * ENTERPRISE PRODUCTS - Separate category (not managed here, external links)
 */
export const ENTERPRISE = {
  VCN: {
    id: 'vcn',
    name: 'VCN',
    displayName: 'VCN',
    icon: 'VpnLockIcon',
  },
  GATEWAY_SNIFFER: {
    id: 'gateway-sniffer',
    name: 'GatewaySniffer',
    displayName: 'GatewaySniffer',
    icon: 'SecurityIcon',
  },
  GATE_KEEPER_X: {
    id: 'gate-keeper-x',
    name: 'GateKeeperX',
    displayName: 'GateKeeperX',
    icon: 'VpnLockIcon',
  },
  GUARDIAN_INFRA: {
    id: 'guardian-infra',
    name: 'Guardian Infra',
    displayName: 'Guardian Infra',
    icon: 'StorageIcon',
  },
} as const;

/**
 * Type definitions for product objects
 */
export type ProductKey = keyof typeof PRODUCTS;
export type ExternalProductKey = keyof typeof EXTERNAL_PRODUCTS;
export type DevProductKey = keyof typeof PRODUCTS_IN_DEVELOPMENT;
export type ServiceKey = keyof typeof SERVICES;
export type EnterpriseKey = keyof typeof ENTERPRISE;
export type CliComponentKey = keyof typeof CLI_COMPONENTS;
export type ReferralProgramKey = keyof typeof REFERRAL_PROGRAM;

export type Product = (typeof PRODUCTS)[ProductKey];
export type ExternalProduct = (typeof EXTERNAL_PRODUCTS)[ExternalProductKey];
export type DevProduct = (typeof PRODUCTS_IN_DEVELOPMENT)[DevProductKey];
export type Service = (typeof SERVICES)[ServiceKey];
export type EnterpriseProduct = (typeof ENTERPRISE)[EnterpriseKey];
export type CliComponent = (typeof CLI_COMPONENTS)[CliComponentKey];
export type ReferralProgram = typeof REFERRAL_PROGRAM;

/**
 * Union types for all product categories
 */
export type AnyProduct = Product | ExternalProduct | DevProduct | Service | EnterpriseProduct | CliComponent | ReferralProgram;
export type SignupProduct = Product;  // Only internal PRODUCTS support signup (not Referral)
export type InternalProduct = Product;  // For dashboard, features, subscriptions, etc.
export type SubscriptionProduct = Product;  // Products with independent subscriptions
  // Fraud Prevention, Caption Engine, Distro Engine, Guardian CLI

/**
 * Combined exports for convenience
 */
export const ALL_PRODUCTS = {
  ...PRODUCTS,
  ...{ REFERRAL_PROGRAM },  // Include Referral Program separately (not subscription-based)
  ...EXTERNAL_PRODUCTS,
  ...PRODUCTS_IN_DEVELOPMENT,
  ...SERVICES,
  ...ENTERPRISE,
  ...CLI_COMPONENTS,
} as const;

export const ALL_EXTERNAL = {
  ...EXTERNAL_PRODUCTS,
} as const;

export const ALL_IN_DEVELOPMENT = {
  ...PRODUCTS_IN_DEVELOPMENT,
} as const;

export const ALL_SERVICES = {
  ...SERVICES,
} as const;

/**
 * Navigation structure - groupings for UI
 */
export const NAVIGATION_STRUCTURE = {
  products: Object.values(PRODUCTS),
  external: Object.values(EXTERNAL_PRODUCTS),
  development: Object.values(PRODUCTS_IN_DEVELOPMENT),
  services: Object.values(SERVICES),
  enterprise: Object.values(ENTERPRISE),
} as const;
